import { HttpQuery, SearchQuery } from "@/interfaces/httpQuery";
import { store } from "@/internal";
import { AxiosResponse } from "axios";
import { TableAction, TablePagination } from "helix-vue-components";
import Vue from "vue";

type PaginationCallback = (...arg: any[]) => void;

/**
 * abstract service to manage http request and general services configurations
 */
export default abstract class HttpService extends Vue {
  /**
   * path to call in the backend
   * @var string uri
   */
  protected uri!: string;

  /**
   * dispatcher to pagination actions
   * @var string loadDispatcher
   */
  protected loadDispatcher!: string;

  /**
   * default sort for make a request
   * @var string defaultSort
   */
  protected defaultSort!: string;

  /**
   * available fields for make a search action
   * @var SearchQuery[] searchableField
   */
  protected searchableField!: SearchQuery[];

  /**
   * default pagination definition
   * @var Pagination pagination
   */
  protected pagination: TablePagination = {
    totalItems: 0,
    itemsPerPage: 10,
    itemsPerPageOptions: [5, 10, 20],
    currentPage: 1,
    from: 1,
    to: 10
  };

  /**
   * default query for make a request
   * @var query
   */
  protected query: HttpQuery = {
    sort: this.defaultSort,
    page: this.pagination.currentPage,
    per_page: this.pagination.itemsPerPage
  };

  /**
   * @return Promise
   * @param query
   */
  public async get(query: object | null = null): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "GET",
      url: this.uri,
      params: query || this.query
    });
    this.setPagination(response.data);
    return response.data;
  }

  public async getWithHeaders(headers: object): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "GET",
      url: this.uri,
      headers
    });
    return response.data;
  }
  /**
   * @return Promise
   * @param id
   */
  public async findById(id: number, query?: object): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "GET",
      url: `${this.uri}/${id}`,
      params: query
    });
    return response.data.data;
  }

  /**
   * @param model
   * @return Promise<Array>
   */
  public async post(model: any): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "POST",
      url: this.uri,
      data: model
    });
    return response.data;
  }

  public async patch(model?: any): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "PATCH",
      url: this.uri,
      data: model
    });
    return response.data;
  }
  /**
   * @param model
   * @param data
   * @return Promise<Array>
   */
  public async put(model: any, data: any): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "PUT",
      url: `${this.uri}/${model.id}`,
      data
    });

    return response.data;
  }

  public async find(id: number): Promise<any> {
    this.query.id = id;
    const customer: any[] = await this.get(this.query);
    const returned = customer[0];
    delete this.query.id;
    return returned;
  }

  /**
   * @param model
   * @return Promise<Array>
   */
  public async delete(model: any): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "DELETE",
      url: `${this.uri}/${model.id}`
    });
    return response.data;
  }

  public async deleteForce(model: any): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "DELETE",
      url: `${this.uri}/${model.id}`,
      data: {
        force_delete: true
      }
    });
    return response.data;
  }

  /**
   * return the actions available for a page
   * @return Promise<TableAction[] | null>
   */
  public async getGeneralActions(): Promise<TableAction[] | null> {
    return null;
  }

  /**
   * return the actions available for a row in a table
   * @return Promise<TableAction[] | null>
   */
  public async getRowActions(): Promise<TableAction[] | null> {
    return null;
  }

  /**
   * @return Promise<Pagination>
   */
  public async getPagination(): Promise<TablePagination> {
    return this.pagination;
  }

  /**
   * @param response
   */
  public setPagination(response: any) {
    this.pagination.totalItems = response.data.total;
    this.pagination.itemsPerPage = +response.data.per_page;
    this.pagination.currentPage = response.data.current_page;
    this.pagination.from = response.data.from;
    this.pagination.to = response.data.to;
  }

  /**
   *
   * @return PaginationCallback
   */
  public paginationAction(): PaginationCallback {
    return arg => {
      this.query.page = arg.currentPage;
      this.query.per_page = arg.itemsPerPage;
      store.dispatch(this.loadDispatcher);
    };
  }

  /**
   * sort action to make a request
   * @param params
   */
  public sortQuery(params: any) {
    const desc: string = params.descending ? "-" : "";
    const sort: string =
      desc + (params.value ? params.value : this.defaultSort);
    this.query.sort = sort;
    store.dispatch(this.loadDispatcher);
  }

  /**
   * make a search in a query
   * @return PaginationCallback
   */
  public searchEvent(): PaginationCallback {
    const self: HttpService = this;
    return (...arg: any[]) => {
      this.resetPagination();
      const formatted: any = {};
      self.searchableField.map(field => {
        formatted[field.type] =
          typeof formatted[field.type] === "undefined"
            ? []
            : formatted[field.type];
        formatted[field.type].push(field.field);
      });
      let query: string = "";
      Object.keys(formatted).forEach(field => {
        query = formatted[field].join("_or_") + "_" + field;
        self.query[`q[${query}]`] = arg[0];
      });
      store.dispatch(this.loadDispatcher);
    };
  }

  public resetPagination() {
    this.query.page = 1;
    this.query.per_page = 10;
  }
}
